import { Card } from '../../../molecules/Card/Card';
import { legendItems, type ChartSeries } from '../../../molecules/ChartFrame/chartTheme';
import { ChartLegend } from '../../../molecules/ChartLegend/ChartLegend';
import { WordCloud, type WordCloudWord } from '../../../organisms/WordCloud/WordCloud';
import { PLOT_HEIGHT, cardState, spanClass, type WidgetBlockProps } from '../shared/cardShell';

/**
 * How many words each cloud draws.
 *
 * **Fifty since 2026-09-26**, where it was twenty, and twelve before that. It is
 * now the size of the whole keyword roster, so in practice **the box does the
 * limiting, not this number** — the packer refuses any position not wholly
 * inside the box, drops what is left over, and reports the count in the
 * accessible name.
 *
 * Measured on the default window, where 47 of the 50 keywords appear: all 47
 * place in a 554x224 slot with no overlaps, and 46 of 47 in a 284x224 one. The
 * topic cloud is unaffected — the Topics chip offers exactly 20 subjects.
 *
 * The cut is the cloud's own, not the presenter's — `WordCloud` takes the whole
 * ranked list and limits it, because `sizeStep` is pinned to the largest word
 * and trimming before the cloud sees the list would not change which word that
 * is. Leaving the cut in one place means the cap and the scale cannot disagree.
 */
export const CLOUD_WORDS = 50;

export type TrendingKeywordsView = {
  /**
   * Every keyword in scope, ranked by mentions: sized by volume, coloured by
   * the sentiment most of its mentions carry, with its English gloss spoken.
   */
  words: WordCloudWord[];
  /** The sentiments present, for the legend. */
  series: ChartSeries[];
  formatValue: (value: number) => string;
};

export type TrendingKeywordsCardProps = WidgetBlockProps & {
  view?: TrendingKeywordsView;
  /** Opens the conversations behind a word (its key, which is its label). */
  onViewDetails?: (key: string) => void;
};

/**
 * Trends' keyword cloud (979:33742), and Overview's (972:21045).
 *
 * The exact words people are typing, in the script they typed them in. Bilingual
 * by design — Bangla and English keywords share one cloud rather than sitting in
 * two lists.
 *
 * **No KPI row**, which is the card as Trends draws it and which Overview's own
 * frame does not contradict: the leftover labels its `KPI` slot carries
 * (`Most valuable type` / `Leading channel`) belong to a different card
 * entirely. A cloud's figures are the words.
 */
export function TrendingKeywordsCard({
  status,
  view,
  span,
  chrome,
  onRetry,
  onViewDetails,
}: TrendingKeywordsCardProps) {
  /* Every keyword row carries a keyword, so words exist exactly when rows do. */
  const hasData = view !== undefined && view.words.length > 0;
  return (
    <Card
      /* Set only when this card is a widget on a custom report — see `CardChrome`. */
      {...chrome}
      className={spanClass(span)}
      emptyLabel="No keywords in this period"
      {...cardState(status, hasData, onRetry)}
      title="Trending keywords"
      helpText="The exact words people are typing, in the script they typed them in, sized by frequency and coloured by sentiment."
      helpSupportingText="Bilingual by design — Bangla and English keywords share one cloud rather than sitting in two lists. Each word carries its English gloss in the readout and to a screen reader."
      chart={
        view && (
          <WordCloud
            onViewDetails={onViewDetails}
            words={view.words}
            limit={CLOUD_WORDS}
            height={PLOT_HEIGHT}
            unit="mentions"
            emptyLabel="No keywords in this period"
            formatValue={view.formatValue}
          />
        )
      }
      legend={view && <ChartLegend items={legendItems(view.series)} />}
    />
  );
}
